import React, { useState } from "react";

import { Badge } from "@/components/primitives/Badge";
import { Card } from "@/components/primitives/Card";
import { RecentFailure } from "@/types/stats";
import { Step } from "@/types";
import { RequestDetailsDialog } from "./RequestDetailsDialog";

interface RecentFailuresTableProps {
  failures: RecentFailure[];
  steps: Step[];
}

export const RecentFailuresTable: React.FC<RecentFailuresTableProps> = ({ failures, steps }) => {
  const [selectedFailure, setSelectedFailure] = useState<RecentFailure | null>(null);

  const selectedStep = selectedFailure ? steps.find((s) => s.id === selectedFailure.step_id) : undefined;

  return (
    <>
      <Card className="p-6">
        <h3 className="mb-1 text-lg font-semibold text-slate-900 dark:text-white">Recent Failures</h3>
        {failures.length === 0 ? (
          <p className="py-10 text-center text-slate-500 dark:text-slate-400">
            No failures in this period. 🎉
          </p>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                <tr>
                  <th className="px-4 py-3">Time</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Attempt</th>
                  <th className="px-4 py-3">Error</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {failures.map((failure) => (
                  <tr
                    key={failure.id}
                    className="cursor-pointer transition-colors hover:bg-slate-50/50 dark:hover:bg-slate-800/30"
                    onClick={() => setSelectedFailure(failure)}
                    title="Click to view request and response details"
                  >
                    <td className="px-4 py-3 font-mono text-slate-900 dark:text-white">
                      {new Date(failure.started_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="neutral">{failure.event_type ?? failure.step_type ?? "—"}</Badge>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="danger">{failure.http_status ?? "failure"}</Badge>
                    </td>
                    <td className="px-4 py-3 font-mono text-slate-900 dark:text-white">
                      {failure.attempt_number}
                    </td>
                    <td className="max-w-[320px] overflow-hidden text-ellipsis whitespace-nowrap px-4 py-3 text-slate-600 dark:text-slate-300">
                      {failure.error_message ?? "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <RequestDetailsDialog
        isOpen={selectedFailure !== null}
        onClose={() => setSelectedFailure(null)}
        run={selectedFailure ? { ...selectedFailure, status: "failure" } : null}
        step={selectedStep}
      />
    </>
  );
};
